import { Alert, Grid2, IconButton, TextField, Typography } from "@mui/material";

import { ChangeEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { Donut, DONUT_INITIAL_VALUES } from "@/pages/catalogs/models/Donut";
import { upsertDonut } from "@/pages/catalogs/repositories/DonutRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Visibility } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
	DonutStatusSelector,
	LocationTreeSelector,
	SectionFamilySelector,
} from "../../../../../shared/selectors";

type Props = {
	donut: Donut | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const DonutDetails = ({ donut, modal, onHide }: Props) => {
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const succesfullyMessage = `The donut was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<Donut>({
		defaultValues: DONUT_INITIAL_VALUES,
		mode: "onChange",
	});

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);
	modal == "create";

	const validateDiameters = (
		value: number | null,
		field: "Diameter" | "NewDiameter" | "ScrapDiameter",
	) => {
		const currentDiameter = getValues("Diameter") || 0;

		if (value === null || value === undefined) return true;

		switch (field) {
			case "NewDiameter":
				return (
					value >= currentDiameter ||
					"Must be greater than or equal to the current diameter"
				);
			case "ScrapDiameter":
				return (
					value < currentDiameter || "Must be less than the current diameter"
				);
			default:
				return true;
		}
	};

	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};
	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertDonut, {
		onSuccess: async (id: number) => {
			setNotificationMessage(succesfullyMessage);
			await queryClient.invalidateQueries(["donuts"]);
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Donut> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (donut?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (modal !== "") {
			reset(donut || DONUT_INITIAL_VALUES);
			setIsSubmitLoading(false);
		}
	}, [modal, donut]);

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						donut === null ? "New Donut" : `${donut?.PartName ?? ""}`
					} - Donuts`}
				/>
			)}
			{donut === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a donut or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						{!!donut?.IsInActiveCarrier ? (
							<Grid2 size={{ xs: 12 }}>
								<Alert
									severity="warning"
									action={
										<IconButton
											component={Link}
											to={`/catalogs/universal-rolls/${donut.UniversalRollPartId}`}
											target="_blank"
											size="small"
											title={`See in universal rolls`}
										>
											<Visibility fontSize="small" />
										</IconButton>
									}
								>
									This donut is in Universal Roll {donut?.UniversalRollName} in
									Rollset {donut?.RollsetLetter} (Section:{" "}
									{donut?.RollsetSectionName}, Stand: {donut?.RollsetStandName})
									which is assigned to carrier{" "}
									<strong>{donut?.CarrierName}</strong> (Status:{" "}
									{donut?.CarrierStatusName}).
								</Alert>
							</Grid2>
						) : null}
						{!!donut?.IsInActiveRollset && !donut?.IsInActiveCarrier ? (
							<Grid2 size={{ xs: 12 }}>
								<Alert
									severity="info"
									action={
										<IconButton
											component={Link}
											to={`/catalogs/universal-rolls/${donut.UniversalRollPartId}`}
											target="_blank"
											size="small"
											title={`See in universal rolls`}
										>
											<Visibility fontSize="small" />
										</IconButton>
									}
								>
									This donut is in Universal Roll {donut?.UniversalRollName} in
									an active Rollset {donut?.RollsetLetter} (Section:{" "}
									{donut?.RollsetSectionName}, Stand: {donut?.RollsetStandName}
									).
								</Alert>
							</Grid2>
						) : null}
						<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-name-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="DonutStatusCode"
								rules={{ required: "Status is required" }}
								control={control}
								render={({ field, fieldState: { error } }) => (
									<DonutStatusSelector
										label="Status"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										disabled={modal === "delete"}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="SectionFamilyId"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<SectionFamilySelector
										label="Section Family"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
										fieldError={error}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="Width"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Width"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-width-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="NewDiameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "NewDiameter"),
									},
									deps: ["Diameter", "ScrapDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Initial Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-new-diameter-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="Diameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "Diameter"),
									},
									deps: ["NewDiameter", "ScrapDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Current Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-diameter-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="ScrapDiameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "ScrapDiameter"),
									},
									deps: ["Diameter", "NewDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Scrap Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										disabled={modal === "delete"}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "donut-scrap-diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ sm: 12, md: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
